// src/pages/Cohort/CohortDashboard.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, Tag, Tooltip, Spin, Empty,
  Button, Select, Input, DatePicker, Avatar,
  Popconfirm, message, Badge, Progress,
} from "antd";
import {
  TeamOutlined, ReloadOutlined, SearchOutlined,
  CheckCircleOutlined, ClockCircleOutlined, WarningOutlined,
  UserDeleteOutlined, EyeOutlined, PhoneOutlined,
  DeleteOutlined, ClearOutlined, DownOutlined, UpOutlined,
  CalendarOutlined, ArrowRightOutlined, TrophyOutlined,
  FireOutlined,
} from "@ant-design/icons";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Cell, Legend,
} from "recharts";
import dayjs from "dayjs";
import api from "../../services/api";
import employeeService from "../../services/employeeService";
import StatusBadge from "../../components/StatusBadge";
import CohortDay10Summary from "./CohortDay10Summary";
import { useAllSchedules, useScheduleForCohort } from "../../context/MasterDataContext";

const C = {
  navy:"#1B2D6B", blue:"#1E6FBF", lblue:"#EAF1F8",
  green:"#16a34a", lgreen:"#F0FDF4",
  red:"#dc2626",   lred:"#FEF2F2",
  amber:"#b45309", lamber:"#FFFBEB",
  purple:"#7c3aed",lpurple:"#f5f3ff",
  text1:"#1a2d4a", text2:"#4a6580", text3:"#7a96b0",
  border:"#dce6f0", bg:"#f0f4f8", white:"#ffffff",
};

const CALL_DAYS_MAP = { day10:10,day30:30,day60:60,day90:90,day120:120,day150:150,day180:180 };

// Legacy cutoff — employees who joined before 01-Dec-2025
// are considered journey-complete (for record only, no pending calls)
const JOURNEY_CUTOFF = '2025-12-01';
const isLegacy = (emp) => emp?.dateOfJoining && emp.dateOfJoining < JOURNEY_CUTOFF;

function getCallStatus(emp, callKey) {
  // Legacy employees — all stages closed, journey completed for record
  if (isLegacy(emp)) return "Completed";
  const call = emp.calls?.find(c => c.callKey === callKey);
  if (call) return call.status || "Upcoming";
  if (!emp.dateOfJoining || !CALL_DAYS_MAP[callKey]) return "Upcoming";
  const daysUntilDue = dayjs(emp.dateOfJoining).add(CALL_DAYS_MAP[callKey],"day").diff(dayjs(),"day");
  // Pending = due today (daysUntilDue <= 0) or already past (daysUntilDue < 0)
  // Upcoming = due in the future (daysUntilDue > 0)
  return daysUntilDue > 0 ? "Upcoming" : "Pending";
}

// ── Shared atoms ─────────────────────────────────────────────────
const PctBar = ({pct=0,color=C.blue,height=6,showLabel=true}) => (
  <div style={{display:"flex",alignItems:"center",gap:6}}>
    <div style={{flex:1,height,borderRadius:99,background:"#e2e8f0",overflow:"hidden"}}>
      <div style={{width:`${Math.min(pct,100)}%`,height:"100%",
        background:color,borderRadius:99,transition:"width .4s"}}/>
    </div>
    {showLabel && <span style={{fontSize:11,fontWeight:700,color,minWidth:30}}>{pct}%</span>}
  </div>
);

const KpiTile = ({icon,label,value,sub,color,bg,note,onClick}) => (
  <div
    onClick={onClick}
    style={{
      background:C.white, borderRadius:14, padding:"18px 20px",
      border:`1.5px solid ${color}28`,
      boxShadow:`0 2px 12px ${color}12`,
      display:"flex",flexDirection:"column",gap:8,
      cursor: onClick ? "pointer" : "default",
      transition:"box-shadow 0.2s, transform 0.15s",
      ...(onClick && { ':hover':{ boxShadow:`0 4px 20px ${color}28`, transform:"translateY(-1px)" } }),
    }}
    onMouseEnter={onClick ? e => { e.currentTarget.style.boxShadow=`0 6px 24px ${color}30`; e.currentTarget.style.transform="translateY(-2px)"; } : undefined}
    onMouseLeave={onClick ? e => { e.currentTarget.style.boxShadow=`0 2px 12px ${color}12`; e.currentTarget.style.transform="translateY(0)"; } : undefined}
  >
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
      <div style={{fontSize:11,fontWeight:700,color:C.text3,
        textTransform:"uppercase",letterSpacing:"0.8px"}}>{label}</div>
      <div style={{width:36,height:36,borderRadius:10,background:bg||`${color}15`,
        display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color}}>
        {icon}
      </div>
    </div>
    <div style={{fontSize:32,fontWeight:800,color,lineHeight:1,letterSpacing:"-1px"}}>{value??0}</div>
    {sub  && <div style={{fontSize:11,color:C.text3}}>{sub}</div>}
    {note && <div style={{fontSize:11,fontWeight:700,color}}>{note}</div>}
    {onClick && <div style={{fontSize:10,color:color,fontWeight:600,marginTop:2}}>🔗 Click to view list →</div>}
  </div>
);

const ChartTip = ({active,payload,label}) => {
  if(!active||!payload?.length) return null;
  return (
    <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,
      padding:"10px 14px",boxShadow:"0 8px 24px rgba(0,0,0,0.1)",fontSize:12}}>
      <div style={{fontWeight:700,color:C.text1,marginBottom:6}}>{label}</div>
      {payload.map(p=>(
        <div key={p.name} style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
          <span style={{width:8,height:8,borderRadius:2,background:p.color,display:"inline-block"}}/>
          <span style={{color:C.text2}}>{p.name}:</span>
          <span style={{fontWeight:700,color:C.text1}}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ── Monthly Cohort View ───────────────────────────────────────────
function MonthlyCohortView({ cohort, allData }) {
  const [tab, setTab] = React.useState("monthly");

  const sortMonth = m => { const d=dayjs(m,"MMM YYYY"); return d.isValid()?d.valueOf():0; };

  const allMonthsMap = {};
  (allData||[]).forEach(c=>{
    (c.monthly||[]).forEach(m=>{
      if(!allMonthsMap[m.month])
        allMonthsMap[m.month]={month:m.month,target:0,achieved:0,pending:0,noResponse:0};
      allMonthsMap[m.month].target     += m.target;
      allMonthsMap[m.month].achieved   += m.achieved;
      allMonthsMap[m.month].pending    += m.pending;
      allMonthsMap[m.month].noResponse += m.noResponse;
    });
  });
  const allMonths = Object.values(allMonthsMap)
    .sort((a,b)=>sortMonth(a.month)-sortMonth(b.month));

  const displayData = cohort==="all"
    ? allMonths
    : (cohort?.monthly||[]).slice().sort((a,b)=>sortMonth(a.month)-sortMonth(b.month));

  const title = cohort==="all"
    ? "All Cohorts — Monthly Summary"
    : `${cohort.cohortName}${cohort.cohortDate?" · "+dayjs(cohort.cohortDate).format("DD MMM YYYY"):""}`;

  const grand = {
    target:     displayData.reduce((s,m)=>s+m.target,0),
    achieved:   displayData.reduce((s,m)=>s+m.achieved,0),
    pending:    displayData.reduce((s,m)=>s+m.pending,0),
    noResponse: displayData.reduce((s,m)=>s+m.noResponse,0),
  };
  grand.pct = grand.target>0?Math.round((grand.achieved/grand.target)*100):0;

  const cohortSummary = cohort==="all" ? (allData||[]).map(c=>{
    const tot=  (c.monthly||[]).reduce((s,m)=>s+m.target,0);
    const ach=  (c.monthly||[]).reduce((s,m)=>s+m.achieved,0);
    const pend= (c.monthly||[]).reduce((s,m)=>s+m.pending,0);
    const nor=  (c.monthly||[]).reduce((s,m)=>s+m.noResponse,0);
    return {
      cohortName:c.cohortName, cohortDate:c.cohortDate,
      enrolled:c.total, target:tot, achieved:ach, pending:pend, noResponse:nor,
      pct:tot>0?Math.round((ach/tot)*100):0,
    };
  }).sort((a,b)=>b.target-a.target) : [];

  const pcol = p => p>=80?C.green:p>=50?C.amber:p>0?C.red:C.text3;
  const pbg  = p => p>=80?C.lgreen:p>=50?C.lamber:p>0?C.lred:"#f1f5f9";

  return (
    <div style={{marginBottom:20}}>
      {/* Title */}
      <div style={{fontWeight:800,fontSize:18,color:C.text1,marginBottom:4,
        display:"flex",alignItems:"center",gap:8}}>
        📅 {title}
      </div>
      <div style={{fontSize:12,color:C.text3,marginBottom:20}}>
        {displayData.length} months · {grand.target} total calls scheduled
      </div>

      {/* KPI strip */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        {[
          {label:"Target Calls",     value:grand.target,     color:C.blue,  icon:"🎯",
           sub:"Calls due this period"},
          {label:"Achieved",         value:grand.achieved,   color:C.green, icon:"✅",
           note:`${grand.pct}% completion rate`},
          {label:"Pending",          value:grand.pending,    color:C.amber, icon:"⏳",
           note:grand.pending>0?"⚠️ Action required":"✅ All clear"},
          {label:"No Response",      value:grand.noResponse, color:C.red,   icon:"🚫",
           note:grand.noResponse>0?"🚨 Follow up needed":"✅ None"},
        ].map(k=>(
          <KpiTile key={k.label} {...k}/>
        ))}
      </div>

      {/* Overall achievement bar */}
      <div style={{background:C.white,borderRadius:12,padding:"16px 20px",
        border:`1px solid ${C.border}`,marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",marginBottom:10}}>
          <span style={{fontWeight:700,fontSize:13,color:C.text1}}>
            Overall Achievement Rate
          </span>
          <span style={{fontSize:16,fontWeight:800,color:pcol(grand.pct)}}>
            {grand.achieved} / {grand.target} calls &nbsp;·&nbsp; {grand.pct}%
          </span>
        </div>
        <div style={{height:14,borderRadius:99,background:"#e2e8f0",overflow:"hidden",
          position:"relative"}}>
          <div style={{
            position:"absolute",left:0,top:0,height:"100%",borderRadius:99,
            width:`${Math.min(grand.pct,100)}%`,
            background:`linear-gradient(90deg,${pcol(grand.pct)},${pcol(grand.pct)}cc)`,
            transition:"width .5s",
          }}/>
        </div>
        <div style={{display:"flex",gap:20,marginTop:10,flexWrap:"wrap"}}>
          {[
            {label:"✅ Achieved",  val:grand.achieved,   color:C.green},
            {label:"⏳ Pending",   val:grand.pending,    color:C.amber},
            {label:"🚫 No Resp",   val:grand.noResponse, color:C.red},
            {label:"🎯 Target",    val:grand.target,     color:C.blue},
          ].map(x=>(
            <div key={x.label} style={{fontSize:12,color:C.text2}}>
              <span style={{fontWeight:800,color:x.color,fontSize:15}}>{x.val}</span>
              &nbsp;{x.label}
            </div>
          ))}
        </div>
      </div>

      {/* Tab switcher (all view only) */}
      {cohort==="all" && (
        <div style={{display:"flex",gap:8,marginBottom:16}}>
          {[
            {key:"monthly",label:"📅 Month-wise Summary"},
            {key:"cohort", label:"👥 Cohort-wise Summary"},
          ].map(t=>(
            <button key={t.key} onClick={()=>setTab(t.key)} style={{
              padding:"8px 20px",borderRadius:8,fontWeight:700,fontSize:13,
              cursor:"pointer",border:"none",transition:"all .2s",
              background:tab===t.key?C.navy:C.white,
              color:tab===t.key?"#fff":C.text2,
              boxShadow:tab===t.key?"0 4px 12px rgba(27,45,107,0.25)":
                `0 1px 4px rgba(0,0,0,0.06)`,
            }}>{t.label}</button>
          ))}
        </div>
      )}

      {/* Monthly table */}
      {(cohort!=="all"||tab==="monthly") && (
        <div style={{background:C.white,borderRadius:14,border:`1px solid ${C.border}`,
          overflow:"hidden",marginBottom:16}}>
          <div style={{
            padding:"14px 20px",fontWeight:700,fontSize:14,color:"#fff",
            background:`linear-gradient(135deg,${C.navy},#2a4a9f)`,
            display:"flex",justifyContent:"space-between",alignItems:"center",
          }}>
            <span>📊 Month-wise Call Performance</span>
            <span style={{fontSize:12,fontWeight:400,opacity:.7}}>
              {displayData.length} months
            </span>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:"#f8fafc"}}>
                {["Month","Target","Achieved","Pending","No Response","Achievement %"].map(h=>(
                  <th key={h} style={{padding:"10px 16px",fontSize:11,fontWeight:700,
                    color:C.text3,textTransform:"uppercase",letterSpacing:"0.5px",
                    borderBottom:`1px solid ${C.border}`,
                    textAlign:h==="Month"?"left":"center"}}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayData.map((m,i)=>{
                const pct=m.target>0?Math.round((m.achieved/m.target)*100):0;
                const col=pcol(pct), bg=pbg(pct);
                return (
                  <tr key={m.month} style={{
                    borderBottom:`1px solid ${C.border}`,
                    background:i%2===0?C.white:"#fafcff",
                  }}>
                    <td style={{padding:"11px 16px",fontWeight:700,
                      fontSize:13,color:C.text1}}>{m.month}</td>
                    <td style={{padding:"11px 16px",textAlign:"center"}}>
                      <span style={{fontWeight:700,color:C.blue,
                        background:C.lblue,padding:"2px 10px",borderRadius:20,fontSize:12}}>
                        {m.target}
                      </span>
                    </td>
                    <td style={{padding:"11px 16px",textAlign:"center"}}>
                      <span style={{fontWeight:700,color:C.green,
                        background:C.lgreen,padding:"2px 10px",borderRadius:20,fontSize:12}}>
                        {m.achieved}
                      </span>
                    </td>
                    <td style={{padding:"11px 16px",textAlign:"center"}}>
                      {m.pending>0
                        ?<span style={{fontWeight:700,color:C.amber,
                            background:C.lamber,padding:"2px 10px",borderRadius:20,fontSize:12}}>
                            ⏳ {m.pending}
                          </span>
                        :<span style={{color:C.text3,fontSize:12}}>—</span>}
                    </td>
                    <td style={{padding:"11px 16px",textAlign:"center"}}>
                      {m.noResponse>0
                        ?<span style={{fontWeight:700,color:C.red,
                            background:C.lred,padding:"2px 10px",borderRadius:20,fontSize:12}}>
                            🚫 {m.noResponse}
                          </span>
                        :<span style={{color:C.text3,fontSize:12}}>—</span>}
                    </td>
                    <td style={{padding:"11px 20px",minWidth:160}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{flex:1,height:8,borderRadius:99,
                          background:"#e2e8f0",overflow:"hidden"}}>
                          <div style={{width:`${Math.min(pct,100)}%`,height:"100%",
                            borderRadius:99,background:col,transition:"width .4s"}}/>
                        </div>
                        <span style={{fontSize:13,fontWeight:800,color:col,minWidth:36}}>
                          {pct}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{background:C.navy}}>
                <td style={{padding:"12px 16px",fontWeight:800,color:"#fff",fontSize:13}}>
                  TOTAL
                </td>
                <td style={{padding:"12px 16px",textAlign:"center",fontWeight:800,color:"#fff",fontSize:14}}>
                  {grand.target}
                </td>
                <td style={{padding:"12px 16px",textAlign:"center",fontWeight:800,color:"#4ade80",fontSize:14}}>
                  {grand.achieved}
                </td>
                <td style={{padding:"12px 16px",textAlign:"center",fontWeight:800,color:"#fbbf24",fontSize:14}}>
                  {grand.pending>0?grand.pending:"—"}
                </td>
                <td style={{padding:"12px 16px",textAlign:"center",fontWeight:800,color:"#f87171",fontSize:14}}>
                  {grand.noResponse>0?grand.noResponse:"—"}
                </td>
                <td style={{padding:"12px 20px",minWidth:160}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{flex:1,height:8,borderRadius:99,
                      background:"rgba(255,255,255,0.2)",overflow:"hidden"}}>
                      <div style={{width:`${Math.min(grand.pct,100)}%`,height:"100%",
                        borderRadius:99,
                        background:grand.pct>=80?"#4ade80":grand.pct>=50?"#fbbf24":"#f87171"}}/>
                    </div>
                    <span style={{fontSize:13,fontWeight:800,color:"#fff",minWidth:36}}>
                      {grand.pct}%
                    </span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Cohort summary table */}
      {cohort==="all" && tab==="cohort" && (
        <div style={{background:C.white,borderRadius:14,border:`1px solid ${C.border}`,
          overflow:"hidden"}}>
          <div style={{
            padding:"14px 20px",fontWeight:700,fontSize:14,color:"#fff",
            background:`linear-gradient(135deg,${C.blue},#1a5fa3)`,
            display:"flex",justifyContent:"space-between",alignItems:"center",
          }}>
            <span>👥 Cohort-wise Call Performance Summary</span>
            <span style={{fontSize:12,fontWeight:400,opacity:.7}}>
              {cohortSummary.length} cohorts
            </span>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:"#f8fafc"}}>
                {["Cohort","Date","Enrolled","Target","Achieved","Pending","No Resp","Achievement"].map(h=>(
                  <th key={h} style={{padding:"10px 14px",fontSize:11,fontWeight:700,
                    color:C.text3,textTransform:"uppercase",letterSpacing:"0.5px",
                    borderBottom:`1px solid ${C.border}`,
                    textAlign:["Cohort","Date"].includes(h)?"left":"center"}}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cohortSummary.map((r,i)=>{
                const col=pcol(r.pct);
                return (
                  <tr key={i} style={{borderBottom:`1px solid ${C.border}`,
                    background:i%2===0?C.white:"#fafcff"}}>
                    <td style={{padding:"10px 14px",fontWeight:700,
                      fontSize:13,color:C.text1,minWidth:130}}>{r.cohortName}</td>
                    <td style={{padding:"10px 14px",fontSize:12,color:C.text2}}>
                      {r.cohortDate?dayjs(r.cohortDate).format("DD MMM YY"):"—"}
                    </td>
                    <td style={{padding:"10px 14px",textAlign:"center",
                      fontWeight:700,color:C.blue}}>{r.enrolled}</td>
                    <td style={{padding:"10px 14px",textAlign:"center"}}>
                      <span style={{fontWeight:700,color:C.blue,
                        background:C.lblue,padding:"2px 8px",borderRadius:20,fontSize:12}}>
                        {r.target}
                      </span>
                    </td>
                    <td style={{padding:"10px 14px",textAlign:"center"}}>
                      <span style={{fontWeight:700,color:C.green,
                        background:C.lgreen,padding:"2px 8px",borderRadius:20,fontSize:12}}>
                        {r.achieved}
                      </span>
                    </td>
                    <td style={{padding:"10px 14px",textAlign:"center"}}>
                      {r.pending>0
                        ?<span style={{fontWeight:700,color:C.amber,
                            background:C.lamber,padding:"2px 8px",borderRadius:20,fontSize:12}}>
                            {r.pending}
                          </span>
                        :<span style={{color:C.text3}}>—</span>}
                    </td>
                    <td style={{padding:"10px 14px",textAlign:"center"}}>
                      {r.noResponse>0
                        ?<span style={{fontWeight:700,color:C.red,
                            background:C.lred,padding:"2px 8px",borderRadius:20,fontSize:12}}>
                            {r.noResponse}
                          </span>
                        :<span style={{color:C.text3}}>—</span>}
                    </td>
                    <td style={{padding:"10px 16px",minWidth:150}}>
                      <PctBar pct={r.pct} color={col}/>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{background:C.navy}}>
                <td colSpan={2} style={{padding:"11px 14px",fontWeight:800,color:"#fff",fontSize:13}}>
                  TOTAL
                </td>
                <td style={{padding:"11px 14px",textAlign:"center",fontWeight:800,color:"#fff",fontSize:13}}>
                  {cohortSummary.reduce((s,r)=>s+r.enrolled,0)}
                </td>
                <td style={{padding:"11px 14px",textAlign:"center",fontWeight:800,color:"#fff",fontSize:13}}>
                  {grand.target}
                </td>
                <td style={{padding:"11px 14px",textAlign:"center",fontWeight:800,color:"#4ade80",fontSize:13}}>
                  {grand.achieved}
                </td>
                <td style={{padding:"11px 14px",textAlign:"center",fontWeight:800,color:"#fbbf24",fontSize:13}}>
                  {grand.pending}
                </td>
                <td style={{padding:"11px 14px",textAlign:"center",fontWeight:800,color:"#f87171",fontSize:13}}>
                  {grand.noResponse}
                </td>
                <td style={{padding:"11px 16px",minWidth:150}}>
                  <PctBar pct={grand.pct}
                    color={grand.pct>=80?"#4ade80":grand.pct>=50?"#fbbf24":"#f87171"}/>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
export default function CohortDashboard() {
  const navigate     = useNavigate();
  const allSchedules  = useAllSchedules();  // all cohort schedules (Pratham + Campus)

  const [data,           setData]           = useState([]);
  const [allEmployees,   setAllEmployees]   = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [search,         setSearch]         = useState("");
  const [filterFrom,     setFilterFrom]     = useState(null);
  const [filterTo,       setFilterTo]       = useState(null);
  const [filterStatus,   setFilterStatus]   = useState("all");
  const [expandedKey,    setExpandedKey]    = useState(null);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [view,           setView]           = useState("cohorts"); // "cohorts" | "monthly" | "day10"
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [showHistorical, setShowHistorical] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cohortRes, empRes] = await Promise.all([
        api.get("/cohort/stats"),
        api.get("/employees", { params: { page: 1, limit: 2000 } }),
      ]);
      // Handle both direct array and {data:[...]} wrapper from NestJS interceptors
      const payload = cohortRes.data;
      const cohorts = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      setData(cohorts);
      setAllEmployees(empRes.data?.data || empRes.data || []);
    } catch (err) {
      console.error("[CohortDashboard] load error:", err);
      setData([]);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Oct'25/Nov'25 split ──────────────────────────────────────
  const DEC_2025_MS = dayjs('2025-12-01').valueOf();
  const mainData = data.filter(c => !c.cohortDate || dayjs(c.cohortDate).valueOf() >= DEC_2025_MS);
  const histData = data.filter(c => c.cohortDate && dayjs(c.cohortDate).valueOf() < DEC_2025_MS);

  // ── Filtered resigned count (After180 / CCD / Venus / Y Code excluded) ──
  const CLOSED_DIVS_C  = new Set(['ccd', 'venus']);
  const MAY_2026_MS_C  = dayjs('2026-05-01').valueOf();
  const isCohortExcluded = e => {
    const doj = e.dateOfJoining || e.cohortDate;
    const div = (e.division || '').trim().toLowerCase();
    if (CLOSED_DIVS_C.has(div) && doj && dayjs(doj).valueOf() > DEC_2025_MS &&
        e.resignedDate && dayjs(e.resignedDate).valueOf() >= MAY_2026_MS_C) return true;
    if (doj && e.resignedDate && dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180) return true;
    if ((e.empCode || '').toUpperCase().startsWith('Y')) return true;
    return false;
  };
  const filteredResignedCount = allEmployees.filter(e => {
    if (!e.isResigned) return false;
    const doj = e.dateOfJoining || e.cohortDate;
    if (!doj || dayjs(doj).valueOf() < DEC_2025_MS) return false;
    return !isCohortExcluded(e);
  }).length;
  // All Dec'25+ resigned (including excluded) — used for true active count
  const decPlusAllResignedC = allEmployees.filter(e => {
    if (!e.isResigned) return false;
    const doj = e.dateOfJoining || e.cohortDate;
    return doj && dayjs(doj).valueOf() >= DEC_2025_MS;
  }).length;

  // Employees who completed the 180-day journey (Dec'25+):
  //   • Active with tenure ≥ 180 days
  //   • Resigned whose tenure AT resignation was ≥ 180 days
  const decPlusCompleted180C = allEmployees.filter(e => {
    const doj = e.dateOfJoining || e.cohortDate;
    if (!doj || dayjs(doj).valueOf() < DEC_2025_MS) return false;
    if (!e.isResigned) return dayjs().diff(dayjs(doj), 'day') >= 180;
    return e.resignedDate && dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180;
  }).length;
  const decPlusActiveC_direct = allEmployees.filter(e => {
    if (e.isResigned) return false;
    const doj = e.dateOfJoining || e.cohortDate;
    return doj && dayjs(doj).valueOf() >= DEC_2025_MS;
  }).length;

  // Calls Pending: exactly matches EmployeeList "Overdue / Pending" filter
  //   • Dec'25+ joiners only
  //   • Not resigned
  //   • Tenure < 180 days (journey NOT yet completed — matches isLegacyEmployee check)
  //   • At least one call stage is due (today or past) AND not yet logged (or logged Pending/Missed)
  //   • "No Response" calls are NOT counted as pending (same as EmployeeList)
  const todayStrC = dayjs().format('YYYY-MM-DD');
  const decPlusPendingC = allEmployees.filter(e => {
    if (e.isResigned) return false;
    const doj = e.dateOfJoining || e.cohortDate;
    if (!doj || dayjs(doj).valueOf() < DEC_2025_MS) return false;
    // Exclude journey-completed employees (tenure >= 180 days) — same as isLegacyEmployee
    if (dayjs().diff(dayjs(doj), 'day') >= 180) return false;
    // Resolve cohort-specific stages (same as EmployeeList)
    const cohortName = (e.cohortName || '').trim().toLowerCase();
    const sched  = allSchedules.find(s => cohortName === s.scheduleKey || cohortName.startsWith(s.scheduleKey));
    const stages = sched?.stages ?? allSchedules[0]?.stages ?? [];
    return stages.some(({ key, day }) => {
      const call   = e.calls?.find(c => c.callKey === key);
      const dueStr = dayjs(doj).add(day, 'day').format('YYYY-MM-DD');
      if (call) return call.status === 'Pending' || call.status === 'Missed';
      return dueStr <= todayStrC; // due today or overdue and not logged
    });
  }).length;

  // ── Filter ───────────────────────────────────────────────────
  const filtered = mainData.filter(c => {
    if (search && !c.cohortName.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterFrom && c.cohortDate && dayjs(c.cohortDate).isBefore(filterFrom,"day")) return false;
    if (filterTo   && c.cohortDate && dayjs(c.cohortDate).isAfter(filterTo,  "day")) return false;
    if (filterStatus==="pending")    return c.pending    > 0;
    if (filterStatus==="noResponse") return c.noResponse > 0;
    if (filterStatus==="completed")
      return c.stageBreakdown?.every(s=>s.completionPct===100);
    return true;
  });

  const reduceTotals = arr => arr.reduce((acc,c)=>({
    total:     acc.total     + c.total,
    attended:  acc.attended  + (c.attended||0),
    enrolled:  acc.enrolled  + (c.enrolled||0),
    deferment: acc.deferment + (c.deferment||0),
    backout:   acc.backout   + (c.backout||0),
    resigned:  acc.resigned  + (c.resigned||0),
    pending:   acc.pending   + c.pending,
    noResponse:acc.noResponse+ c.noResponse,
  }),{total:0,attended:0,enrolled:0,deferment:0,backout:0,resigned:0,pending:0,noResponse:0});

  // ── Totals ───────────────────────────────────────────────────
  const totals     = reduceTotals(mainData);
  const histTotals = reduceTotals(histData);
  // inJourney = total enrolled - counted resigned - completed (matches Dashboard KPI formula)
  const decPlusActiveC = totals.total - filteredResignedCount - decPlusCompleted180C;

  // ── Period targets (aggregated across all cohorts) ────────────
  // today  = calls due exactly today (DOJ + stageDay = today)
  // week   = calls due today through today+6
  // month  = calls due anywhere in current calendar month
  const periodTotals = mainData.reduce((acc,c)=>{
    const p = c.periodStats || {
      today:{target:0,achieved:0,pending:0},
      week: {target:0,achieved:0,pending:0},
      month:{target:0,achieved:0,pending:0},
    };
    return {
      today: {
        target:   acc.today.target   + (p.today?.target   ||0),
        achieved: acc.today.achieved + (p.today?.achieved ||0),
        pending:  acc.today.pending  + (p.today?.pending  ||0),
      },
      week: {
        target:   acc.week.target    + (p.week?.target    ||0),
        achieved: acc.week.achieved  + (p.week?.achieved  ||0),
        pending:  acc.week.pending   + (p.week?.pending   ||0),
      },
      month: {
        target:   acc.month.target   + (p.month?.target   ||0),
        achieved: acc.month.achieved + (p.month?.achieved ||0),
        pending:  acc.month.pending  + (p.month?.pending  ||0),
      },
    };
  },{
    today:{target:0,achieved:0,pending:0},
    week: {target:0,achieved:0,pending:0},
    month:{target:0,achieved:0,pending:0},
  });

  // ── Avg journey (across filtered) ────────────────────────────
  const avgJourney = rec => {
    const stages = rec.stageBreakdown||[];
    return stages.length ? Math.round(stages.reduce((s,st)=>s+st.completionPct,0)/stages.length) : 0;
  };

  const hasFilters = search || filterFrom || filterTo || filterStatus!=="all";

  // ── Chart data: top cohorts by pending ────────────────────────
  const chartData = [...filtered]
    .sort((a,b)=>b.pending-a.pending)
    .slice(0,12)
    .map(c=>({
      cohortName: c.cohortName,
      cohortDate: c.cohortDate ? dayjs(c.cohortDate).format("DD MMM YY") : "",
      label: `${c.cohortName} · ${c.cohortDate ? dayjs(c.cohortDate).format("DD MMM YY") : "No date"}`,
      Pending:   c.pending,
      Completed: c.stageBreakdown?.reduce((s,st)=>s+(st.completed||0),0)||0,
      Enrolled:  c.total,
    }));

  // ── Employee sub-table ────────────────────────────────────────
  const empColumns = [
    { title:"#", width:44,
      render:(_,__,i)=><span style={{color:C.text3,fontSize:11}}>{i+1}</span> },
    { title:"Employee", dataIndex:"empName", width:200,
      render:(v,rec)=>(
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <Avatar size={30} style={{background:"#1E6FBF",fontSize:11,fontWeight:700,flexShrink:0}}>
            {v?.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
          </Avatar>
          <div>
            <div style={{fontWeight:700,fontSize:12,color:C.text1}}>{v}</div>
            <div style={{fontSize:11,color:C.text3}}>{rec.empCode} · {rec.hq}</div>
          </div>
        </div>
      )},
    { title:"DOJ / Day", dataIndex:"dateOfJoining", width:110,
      render:(v)=>{
        if(!v) return "—";
        const days=dayjs().diff(dayjs(v),"day");
        return (
          <div>
            <div style={{fontSize:12,fontWeight:600,color:C.text1}}>
              {dayjs(v).format("DD MMM YY")}
            </div>
            <div style={{fontSize:10,color:days>90?C.amber:C.blue}}>
              Day {days} / 180
            </div>
          </div>
        );
      }},
    // Show first 3 call stages dynamically per employee cohort type
    ...(() => {
      // Get the first cohort's schedule for column headers
      // (employees within a cohort share the same schedule)
      const sched = allSchedules?.[0]?.stages ?? [];
      return sched.slice(0, 3).map(stage => ({
        title: stage.label.replace("Day ","D"),
        width: 80,
        render: (_,rec) => {
          const empSched = (() => {
            const n = (rec.cohortName||"").trim().toLowerCase();
            const f = (allSchedules||[]).find(s => n===s.scheduleKey || n.startsWith(s.scheduleKey));
            return f?.stages ?? sched;
          })();
          const inSchedule = empSched.some(s => s.key === stage.key);
          if (!inSchedule) return <span style={{color:"#e2e8f0",fontSize:11}}>—</span>;
          return <StatusBadge status={getCallStatus(rec, stage.key)} size="small"/>;
        },
      }));
    })(),
    { title:"Journey Progress", width:150,
      render:(_,rec)=>{
        const empSchedule = (() => {
          const name = (rec.cohortName||"").trim().toLowerCase();
          const found = (allSchedules||[]).find(s =>
            name === s.scheduleKey || name.startsWith(s.scheduleKey)
          );
          return found?.stages ?? (allSchedules?.[0]?.stages ?? []);
        })();
        const done = empSchedule.filter(s=>getCallStatus(rec,s.key)==="Completed").length;
        const pct  = empSchedule.length ? Math.round((done/empSchedule.length)*100) : 0;
        return (
          <div>
            <PctBar pct={pct}
              color={pct===100?C.green:pct>50?C.blue:C.amber}/>
            <div style={{fontSize:10,color:C.text3,marginTop:2}}>
              {done}/{empSchedule.length||6} calls done
            </div>
          </div>
        );
      }},
    { title:"Actions", width:100, fixed:"right",
      render:(_,rec)=>(
        <div style={{display:"flex",gap:4}}>
          <Tooltip title="View Profile">
            <Button size="small" type="primary" icon={<EyeOutlined/>}
              style={{background:C.navy,borderColor:C.navy}}
              onClick={()=>navigate(`/employees/${rec.id}`,
                {state:{from:"/cohort",label:"← Cohort Analytics"}})}/>
          </Tooltip>
          {!rec.isResigned && (
            <Tooltip title="Log Call">
              <Button size="small" icon={<PhoneOutlined/>}
                style={{color:C.green,borderColor:C.green+"88"}}
                onClick={()=>navigate(`/employees/${rec.id}`,
                  {state:{from:"/cohort",label:"← Cohort Analytics"}})}/>
            </Tooltip>
          )}
        </div>
      )},
  ];

  // ── Main cohort table columns ─────────────────────────────────
  const cohortColumns = [
    { title:"Cohort / Batch", dataIndex:"cohortName", width:200,
      sorter:(a,b)=>a.cohortName.localeCompare(b.cohortName),
      render:(v,rec)=>(
        <div>
          <div style={{fontWeight:700,fontSize:13,color:C.text1}}>{v}</div>
          <div style={{fontSize:11,color:C.text3,marginTop:2,
            display:"flex",alignItems:"center",gap:4}}>
            <CalendarOutlined style={{fontSize:10}}/>
            {rec.cohortDate?dayjs(rec.cohortDate).format("DD MMM YYYY"):"No date set"}
          </div>
        </div>
      )},
    { title:"Enrolled", dataIndex:"total", width:95, align:"center",
      sorter:(a,b)=>a.total-b.total,
      render:v=>(
        <span style={{fontWeight:800,fontSize:16,color:C.blue}}>{v}</span>
      )},
    { title:"✅ Attended", dataIndex:"attended", width:90, align:"center",
      sorter:(a,b)=>(a.attended||a.active||0)-(b.attended||b.active||0),
      render:(v,rec)=>{
        const val=v??rec.active??0;
        return <span style={{fontWeight:700,fontSize:13,color:C.green}}>{val}</span>;
      }},
    // { title:"🔄 Deferment", dataIndex:"deferment", width:100, align:"center",
    //   sorter:(a,b)=>(a.deferment||0)-(b.deferment||0),
    //   render:v=>v>0
    //     ?<Tooltip title="Joining deferred to next cohort">
    //         <span style={{fontWeight:700,color:C.purple,
    //           background:C.lpurple,padding:"2px 8px",borderRadius:20,fontSize:12}}>
    //           🔄 {v}
    //         </span>
    //       </Tooltip>
    //     :<span style={{color:C.text3,fontSize:12}}>—</span>},
    // { title:"❌ Backout", dataIndex:"backout", width:90, align:"center",
    //   sorter:(a,b)=>(a.backout||0)-(b.backout||0),
    //   render:v=>v>0
    //     ?<Tooltip title="Employee decided NOT to join">
    //         <span style={{fontWeight:700,color:C.red,
    //           background:C.lred,padding:"2px 8px",borderRadius:20,fontSize:12}}>
    //           ❌ {v}
    //         </span>
    //       </Tooltip>
    //     :<span style={{color:C.text3,fontSize:12}}>—</span>},
    { title:"🚪 Resigned", dataIndex:"resigned", width:90, align:"center",
      sorter:(a,b)=>(a.resigned||0)-(b.resigned||0),
      render:v=>v>0
        ?<Tooltip title="Resigned after joining">
            <span style={{fontWeight:700,color:C.red+"dd",
              background:"#fff0f0",padding:"2px 8px",borderRadius:20,fontSize:12}}>
              🚪 {v}
            </span>
          </Tooltip>
        :<span style={{color:C.text3,fontSize:12}}>—</span>},
    { title:"Pending Calls", dataIndex:"pending", width:110, align:"center",
      sorter:(a,b)=>a.pending-b.pending,
      render:v=>v>0
        ?<span style={{fontWeight:700,color:C.amber,
            background:C.lamber,padding:"3px 10px",borderRadius:20,fontSize:12}}>
            ⏳ {v}
          </span>
        :<span style={{color:C.text3,fontSize:12}}>✅ 0</span>},
    { title:"No Response", dataIndex:"noResponse", width:110, align:"center",
      sorter:(a,b)=>a.noResponse-b.noResponse,
      render:v=>v>0
        ?<span style={{fontWeight:700,color:C.red,
            background:C.lred,padding:"3px 10px",borderRadius:20,fontSize:12}}>
            🚫 {v}
          </span>
        :<span style={{color:C.text3,fontSize:12}}>✅ 0</span>},
    { title:"Journey Progress", dataIndex:"stageBreakdown", width:180,
      sorter:(a,b)=>avgJourney(a)-avgJourney(b),
      render:(stages,rec)=>{
        const pct=avgJourney(rec);
        return (
          <div>
            <PctBar pct={pct}
              color={pct===100?C.green:pct>=50?C.blue:C.amber}/>
            <div style={{fontSize:10,color:C.text3,marginTop:3}}>
              avg across {rec.total} employees
            </div>
          </div>
        );
      }},
    { title:"Monthly", width:120, align:"center",
      render:(_,rec)=>(
        <Button size="small" type="link"
          style={{fontSize:12,padding:0,fontWeight:600,color:C.blue}}
          onClick={e=>{
            e.stopPropagation();
            setSelectedCohort(rec);
            setView("monthly");
          }}>
          <CalendarOutlined style={{marginRight:4}}/>View Monthly
        </Button>
      )},
    { title:"", width:44, align:"center",
      render:(_,rec)=>{
        const key=`${rec.cohortName}||${rec.cohortDate}`;
        const isOpen=expandedKey===key;
        return (
          <Button type="text" size="small"
            icon={isOpen
              ?<UpOutlined style={{color:C.blue}}/>
              :<DownOutlined style={{color:C.text3}}/>}
            onClick={e=>{
              e.stopPropagation();
              setExpandedKey(isOpen?null:key);
            }}/>
        );
      }},
  ];

  // ─────────────────────────────────────────────────────────────
  return (
    <div style={{background:C.bg,minHeight:"100vh"}}>

      {/* ══ HEADER ═══════════════════════════════════════════ */}
      <div style={{
        background:`linear-gradient(135deg,${C.navy} 0%,#2a4a9f 100%)`,
        padding:"22px 28px",marginBottom:24,
        boxShadow:"0 4px 20px rgba(27,45,107,0.25)",
      }}>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{fontSize:22,fontWeight:800,color:"#fff",
              letterSpacing:"-0.5px",display:"flex",alignItems:"center",gap:10}}>
              <TeamOutlined/> Cohort Analytics
            </div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",marginTop:3}}>
              {view==="monthly" && selectedCohort ? (
                <button style={{background:"none",border:"none",
                  color:"rgba(255,255,255,0.7)",cursor:"pointer",fontSize:12,
                  padding:0,display:"flex",alignItems:"center",gap:4}}
                  onClick={()=>{setView("cohorts");setSelectedCohort(null);}}>
                  ← Back to all cohorts
                </button>
              ) : (
                <>
                  {hasFilters
                    ?`${filtered.length} of ${mainData.length} cohorts · ${totals.total} employees`
                    :`${mainData.length} cohorts · ${totals.total} enrolled · ${decPlusActiveC} active · ${filteredResignedCount} resigned`}
                </>
              )}
            </div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}
              style={{borderRadius:8,background:"rgba(255,255,255,0.12)",
                border:"1px solid rgba(255,255,255,0.25)",color:"#fff",fontWeight:600}}>
              Refresh
            </Button>
            {histData.length > 0 && (
              <button
                onClick={() => setShowHistorical(v => !v)}
                title="View Oct '25 · Nov '25 historical cohort data"
                style={{
                  padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                  border: `1.5px solid ${showHistorical ? '#fff' : 'rgba(255,255,255,0.45)'}`,
                  background: showHistorical ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.10)',
                  color: '#fff',
                  whiteSpace: 'nowrap',
                }}
              >
                📋 {showHistorical ? 'Hide' : 'Show'} Oct '25 · Nov '25
              </button>
            )}
            <Button
              icon={<CalendarOutlined/>}
              style={{
                borderRadius:8,fontWeight:600,
                background:view==="monthly"&&!selectedCohort
                  ?"rgba(255,255,255,0.3)":"rgba(255,255,255,0.12)",
                border:"1px solid rgba(255,255,255,0.25)",color:"#fff",
              }}
              onClick={()=>{
                if(view==="monthly"&&!selectedCohort){
                  setView("cohorts"); setSelectedCohort(null);
                } else {
                  setView("monthly"); setSelectedCohort("all");
                }
              }}>
              {view==="monthly"&&!selectedCohort?"← All Cohorts":"📅 All Monthly"}
            </Button>
          </div>
        </div>
      </div>

      <div style={{padding:"0 24px 48px"}}>

        {/* Monthly drill-down */}
        {view==="monthly" && selectedCohort && (
          <MonthlyCohortView cohort={selectedCohort} allData={data}/>
        )}

        {view!=="monthly" && <>

          {/* ══ HISTORICAL PANEL (Oct'25 · Nov'25) ══════════ */}
          {showHistorical && histData.length > 0 && (
            <div style={{ marginBottom: 20, borderRadius: 12, overflow: 'hidden',
              border: '1.5px dashed #7c3aed' }}>
              <div style={{ background: '#7c3aed', padding: '8px 16px',
                color: '#fff', fontSize: 13, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 8 }}>
                📋 Oct '25 · Nov '25 — Historical Cohorts
                <span style={{ fontWeight: 400, fontSize: 11, opacity: 0.8 }}>
                  ({histData.length} cohort{histData.length !== 1 ? 's' : ''} · {histTotals.total} enrolled)
                </span>
              </div>
              <div style={{ background: '#faf5ff', padding: '12px 16px' }}>
                <div style={{ display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10 }}>
                  {[
                    { icon: '👥', label: 'Total Enrolled', value: histTotals.total,
                      color: C.navy },
                    { icon: '✅', label: 'Attended', value: histTotals.attended,
                      color: C.green,
                      note: `${histTotals.total > 0 ? Math.round((histTotals.attended / histTotals.total) * 100) : 0}% rate` },
                    { icon: '📋', label: 'Enrolled (Pending)', value: histTotals.enrolled,
                      color: C.blue },
                    { icon: '🔄', label: 'Deferment', value: histTotals.deferment,
                      color: C.purple },
                    { icon: '❌', label: 'Backout', value: histTotals.backout,
                      color: histTotals.backout > 0 ? C.red : C.green,
                      note: `${histTotals.total > 0 ? Math.round((histTotals.backout / histTotals.total) * 100) : 0}% rate` },
                    { icon: '🚪', label: 'Resigned', value: histTotals.resigned,
                      color: histTotals.resigned > 0 ? C.red : C.green,
                      note: `${histTotals.total > 0 ? Math.round((histTotals.resigned / histTotals.total) * 100) : 0}% attrition` },
                    { icon: '📞', label: 'Calls Pending', value: histTotals.pending,
                      color: histTotals.pending > 0 ? C.amber : C.green },
                  ].map((k, i) => (
                    <div key={i} style={{ background: '#fff', borderRadius: 8,
                      padding: '8px 12px', border: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 11, color: C.text3, marginBottom: 2 }}>
                        {k.icon} {k.label}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>
                        {k.value}
                      </div>
                      {k.note && <div style={{ fontSize: 10, color: C.text3, marginTop: 1 }}>{k.note}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ══ KPI CARDS ═══════════════════════════════════ */}
          <div style={{display:"grid",
            gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",
            gap:14,marginBottom:24}}>
            {[
              { icon:<TeamOutlined/>,
                label:"Total Enrolled",       value:totals.total,
                color:C.navy,
                sub:`${mainData.length} cohorts · Dec '25 onwards` },
              { icon:<CheckCircleOutlined/>,
                label:"Active Employees",     value:decPlusActiveC,
                color:C.green, bg:C.lgreen,
                sub:"Currently employed",
                note:`✅ ${totals.total>0?Math.round((decPlusActiveC/totals.total)*100):0}% retention rate` },
              { icon:<CheckCircleOutlined/>,
                label:"✅ Attended",           value:totals.attended,
                color:C.green, bg:C.lgreen,   sub:"Joined & at Pratham",
                note:`${totals.total>0?Math.round((totals.attended/totals.total)*100):0}% attendance rate` },
              { icon:<ClockCircleOutlined/>,
                label:"📋 Enrolled (Pending)",  value:totals.enrolled,
                color:totals.enrolled>0?C.blue:C.green,
                bg:totals.enrolled>0?C.lblue:C.lgreen,
                sub:"Invited, awaiting joining",
                note:totals.enrolled>0?"Confirm Attended/Deferment/Backout":null },
              // { icon:<span style={{fontSize:16}}>🔄</span>,
              //   label:"Deferment",            value:totals.deferment,
              //   color:C.purple, bg:C.lpurple, sub:"Moving to next cohort",
              //   note:totals.deferment>0?`Future batch placement`:null },
              // { icon:<span style={{fontSize:16}}>❌</span>,
              //   label:"Backout",              value:totals.backout,
              //   color:totals.backout>0?C.red:C.green,
              //   bg:totals.backout>0?C.lred:C.lgreen,
              //   sub:"Did not join",
              //   note:totals.backout>0?`${totals.total>0?Math.round((totals.backout/totals.total)*100):0}% backout rate`:"✅ No backouts" },
              { icon:<UserDeleteOutlined/>,
                label:"🚪 Resigned",           value:filteredResignedCount,
                color:filteredResignedCount>0?C.red:C.green,
                bg:filteredResignedCount>0?C.lred:C.lgreen,
                sub:"Left after joining",
                note:filteredResignedCount>0?`${totals.total>0?Math.round((filteredResignedCount/totals.total)*100):0}% attrition`:"✅ None resigned" },
              { icon:<ClockCircleOutlined/>,
                label:"📞 Calls Pending",      value:decPlusPendingC,
                color:decPlusPendingC>0?C.amber:C.green,
                bg:decPlusPendingC>0?C.lamber:C.lgreen,
                sub:"Active employees with overdue calls",
                note:decPlusPendingC>0?"⚠️ Action required":"✅ All clear",
                onClick: decPlusPendingC > 0 ? () => navigate('/employees?tab=active&status=pending') : undefined },
            ].map((k,i)=>(
              <KpiTile key={i} {...k}/>
            ))}
          </div>

          {/* ══ PERIOD TARGET CARDS ══════════════════════════ */}
          <div style={{marginBottom:24}}>
            <div style={{fontSize:14,fontWeight:700,color:C.text1,marginBottom:12,
              display:"flex",alignItems:"center",gap:8}}>
              🎯 Call Targets by Period
              <span style={{fontSize:11,fontWeight:400,color:C.text3}}>
                — How many calls are due and how many are done
              </span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
              {[
                {
                  period:  "Today",
                  icon:    "📅",
                  date:    dayjs().format("DD MMM YYYY"),
                  subNote: periodTotals.today.target===0
                    ? "No calls fall due today"
                    : periodTotals.today.pending>0
                      ? `${periodTotals.today.pending} call${periodTotals.today.pending>1?"s":""} due today not done`
                      : "All today's calls are done!",
                  overdueNote: decPlusPendingC > 0
                    ? `+ ${decPlusPendingC} employees with overdue calls`
                    : null,
                  color:  periodTotals.today.target===0
                    ? C.text3
                    : periodTotals.today.pending>0 ? C.amber : C.green,
                  bg: periodTotals.today.target===0
                    ? "#f1f5f9"
                    : periodTotals.today.pending>0 ? C.lamber : C.lgreen,
                  target:   periodTotals.today.target,
                  achieved: periodTotals.today.achieved,
                  pending:  periodTotals.today.pending,
                },
                {
                  period:  "Next 7 Days",
                  icon:    "📆",
                  date:    `${dayjs().add(1,"day").format("DD MMM")} – ${dayjs().add(6,"day").format("DD MMM YYYY")}`,
                  subNote: null,
                  color:   C.blue,
                  bg:      C.lblue,
                  target:   periodTotals.week.target,
                  achieved: periodTotals.week.achieved,
                  pending:  periodTotals.week.pending,
                },
                {
                  period:  "Current Month",
                  icon:    "🗓️",
                  date:    dayjs().format("MMMM YYYY"),
                  subNote: null,
                  color:   C.purple,
                  bg:      C.lpurple,
                  target:   periodTotals.month.target,
                  achieved: periodTotals.month.achieved,
                  pending:  periodTotals.month.pending,
                },
              ].map(p=>{
                const pct = p.target>0?Math.round((p.achieved/p.target)*100):0;
                const pcol= p.target===0 ? C.text3
                  : pct===100 ? C.green
                  : pct>=80   ? C.green
                  : pct>=50   ? C.amber
                  : C.red;
                return (
                  <div key={p.period} style={{
                    background:C.white,borderRadius:14,padding:"20px 22px",
                    border:`1.5px solid ${p.color}28`,
                    boxShadow:`0 2px 12px ${p.color}10`,
                  }}>
                    {/* Header */}
                    <div style={{display:"flex",justifyContent:"space-between",
                      alignItems:"flex-start",marginBottom:14}}>
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:C.text3,
                          textTransform:"uppercase",letterSpacing:"0.8px"}}>
                          {p.icon} {p.period}
                        </div>
                        <div style={{fontSize:11,color:C.text3,marginTop:3}}>
                          {p.date}
                        </div>
                        {p.subNote && (
                          <div style={{fontSize:10,fontWeight:700,marginTop:4,
                            color:periodTotals.today.pending>0?C.amber:C.green}}>
                            {p.subNote}
                          </div>
                        )}
                        {p.overdueNote && (
                          <div style={{fontSize:10,fontWeight:700,marginTop:2,color:C.red}}>
                            ⚠️ {p.overdueNote}
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontSize:22,fontWeight:800,color:pcol,
                        padding:"4px 12px",borderRadius:20,
                        background:pct>=80?C.lgreen:pct>=50?C.lamber:pct>0?C.lred:"#f1f5f9",
                      }}>
                        {pct}%
                      </div>
                    </div>

                    {/* 3 stat boxes */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
                      {[
                        {label:"Target",  val:p.target,   color:p.color, bg:p.bg},
                        {label:"Done",    val:p.achieved, color:C.green, bg:C.lgreen},
                        {label:"Pending", val:p.pending,  color:p.pending>0?C.amber:C.text3, bg:p.pending>0?C.lamber:"#f1f5f9"},
                      ].map(s=>(
                        <div key={s.label} style={{
                          background:s.bg,borderRadius:10,padding:"10px 8px",
                          textAlign:"center",border:`1px solid ${s.color}22`,
                        }}>
                          <div style={{fontSize:22,fontWeight:800,color:s.color,lineHeight:1}}>
                            {s.val}
                          </div>
                          <div style={{fontSize:10,fontWeight:700,color:C.text3,
                            textTransform:"uppercase",letterSpacing:"0.5px",marginTop:4}}>
                            {s.label}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Progress bar */}
                    <div>
                      <div style={{display:"flex",justifyContent:"space-between",
                        fontSize:11,color:C.text3,marginBottom:5}}>
                        <span>Achievement</span>
                        <span style={{fontWeight:700,color:pcol}}>{p.achieved}/{p.target} calls</span>
                      </div>
                      <div style={{height:8,borderRadius:99,background:"#e2e8f0",overflow:"hidden"}}>
                        <div style={{
                          width:`${Math.min(pct,100)}%`,height:"100%",borderRadius:99,
                          background:`linear-gradient(90deg,${pcol},${pcol}bb)`,
                          transition:"width .5s",
                        }}/>
                      </div>
                    </div>

                    {/* Status note */}
                    <div style={{marginTop:10,fontSize:11,fontWeight:700,
                      color:pcol,textAlign:"center"}}>
                      {p.target===0
                        ? "⬜ No calls scheduled this period"
                        : pct===100
                          ? "🎉 All done! Perfect completion"
                          : pct>=80
                            ? "🟢 On track — great progress"
                            : pct>=50
                              ? "🟡 In progress — keep going"
                              : p.pending>0
                                ? `⚠️ ${p.pending} call${p.pending>1?"s":""} need attention`
                                : "⬜ Not started"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ══ CHART — Top pending cohorts ══════════════════ */}
          {chartData.length>0 && (
            <div style={{background:C.white,borderRadius:14,padding:"20px 24px",
              border:`1px solid ${C.border}`,marginBottom:20,
              boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
              <div style={{marginBottom:16}}>
                <div style={{fontSize:15,fontWeight:700,color:C.text1}}>
                  📊 Cohort Call Status Overview
                </div>
                <div style={{fontSize:12,color:C.text3,marginTop:2}}>
                  Pending vs completed calls — top {chartData.length} cohorts by pending count
                </div>
              </div>
              {/* Custom tick renders cohortName + date on two lines */}
              {(() => {
                const CustomTick = ({ x, y, payload }) => {
                  const item = chartData.find(d=>d.label===payload.value) || {};
                  const name = item.cohortName||payload.value;
                  const date = item.cohortDate||"";
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text x={0} y={0} dy={10} textAnchor="middle"
                        fill={C.text2} fontSize={11} fontWeight={600}>
                        {name.length>10 ? name.slice(0,10)+"…" : name}
                      </text>
                      {date && (
                        <text x={0} y={0} dy={23} textAnchor="middle"
                          fill={C.text3} fontSize={10}>
                          {date}
                        </text>
                      )}
                    </g>
                  );
                };
                return (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}
                  margin={{top:4,right:8,left:-12,bottom:50}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                  <XAxis dataKey="label"
                    tick={<CustomTick/>}
                    axisLine={false} tickLine={false} interval={0}/>
                  <YAxis tick={{fontSize:11,fill:C.text3}} axisLine={false} tickLine={false}/>
                  <RTooltip content={<ChartTip/>}/>
                  <Legend wrapperStyle={{fontSize:12,paddingTop:4}}/>
                  <Bar dataKey="Completed" fill={C.green} radius={[4,4,0,0]} maxBarSize={32}/>
                  <Bar dataKey="Pending"   fill={C.amber} radius={[4,4,0,0]} maxBarSize={32}/>
                </BarChart>
              </ResponsiveContainer>
                );
              })()}
            </div>
          )}

          {/* ══ FILTERS ══════════════════════════════════════ */}
          <div style={{background:C.white,borderRadius:12,padding:"12px 16px",
            marginBottom:16,border:`1px solid ${C.border}`,
            display:"flex",flexWrap:"wrap",gap:8,alignItems:"center"}}>
            <Input prefix={<SearchOutlined style={{color:C.text3}}/>}
              placeholder="Search cohort name…" value={search}
              onChange={e=>setSearch(e.target.value)} allowClear
              style={{width:200,borderRadius:8}}/>
            <DatePicker placeholder="From" format="DD-MMM-YYYY"
              value={filterFrom} onChange={setFilterFrom} allowClear style={{width:140}}/>
            <DatePicker placeholder="To" format="DD-MMM-YYYY"
              value={filterTo} onChange={setFilterTo} allowClear style={{width:140}}/>
            <Select value={filterStatus} onChange={setFilterStatus} style={{width:160}} options={[
              {value:"all",        label:"All Cohorts"},
              {value:"pending",    label:"⏳ Has Pending"},
              {value:"noResponse", label:"🚫 No Response"},
              {value:"completed",  label:"✅ Completed"},
            ]}/>
            {hasFilters && (
              <Button size="small" icon={<ClearOutlined/>}
                style={{color:C.text3}}
                onClick={()=>{
                  setSearch(""); setFilterFrom(null);
                  setFilterTo(null); setFilterStatus("all");
                }}>
                Clear Filters
              </Button>
            )}
            <div style={{marginLeft:"auto",fontSize:12,color:C.text3,fontWeight:600}}>
              Showing {filtered.length} cohort{filtered.length!==1?"s":""}
              {hasFilters&&` (filtered from ${mainData.length})`}
            </div>
          </div>

          {/* ══ COHORT TABLE ══════════════════════════════════ */}
          {loading
            ? <div style={{textAlign:"center",paddingTop:60}}><Spin size="large"/></div>
            : filtered.length===0
              ? <div style={{background:C.white,borderRadius:14,
                  border:`1px solid ${C.border}`,padding:40}}>
                  <Empty description={hasFilters?"No cohorts match filters":"No cohort data"}
                    extra={hasFilters&&(
                      <Button size="small"
                        onClick={()=>{setSearch("");setFilterFrom(null);
                          setFilterTo(null);setFilterStatus("all");}}>
                        Clear Filters
                      </Button>
                    )}/>
                </div>
              : <div style={{background:C.white,borderRadius:14,
                  border:`1px solid ${C.border}`,overflow:"hidden",
                  boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
                  <Table
                    dataSource={filtered}
                    columns={cohortColumns}
                    rowKey={r=>`${r.cohortName}||${r.cohortDate}`}
                    size="small"
                    showSorterTooltip={{title:"Click to sort"}}
                    pagination={{
                      pageSize:20, showSizeChanger:true,
                      pageSizeOptions:[10,20,50],
                      showTotal:t=>(
                        <span style={{color:C.text3,fontSize:12}}>
                          {t} cohort{t!==1?"s":""}
                        </span>
                      ),
                    }}
                    rowClassName={r=>`${r.cohortName}||${r.cohortDate}`===expandedKey
                      ?"cohort-row-active":""}
                    expandable={{
                      expandedRowKeys:expandedKey?[expandedKey]:[],
                      showExpandColumn:false,
                      expandedRowRender:rec=>{
                        const allEmps = rec.employees || [];
                        const pendingEmps = allEmps.filter(e => {
                          if (e.isResigned) return false;
                          // Use the same schedule lookup as empColumns so only applicable
                          // stages are checked — avoids false positives on missing stages
                          const empCohortName = (e.cohortName || rec.cohortName || '').trim().toLowerCase();
                          const found = (allSchedules || []).find(s =>
                            empCohortName === s.scheduleKey || empCohortName.startsWith(s.scheduleKey)
                          );
                          const empSchedule = found?.stages ?? (allSchedules?.[0]?.stages ?? []);
                          return empSchedule.some(stage => {
                            const st = getCallStatus(e, stage.key);
                            // Pending = past-due unlogged or any non-terminal/non-upcoming status
                            return st !== 'Completed' && st !== 'NA' && st !== 'No Response' && st !== 'Upcoming';
                          });
                        });
                        const displayEmps = showPendingOnly ? pendingEmps : allEmps;
                        return (
                        <div style={{padding:"16px 20px",
                          background:"#f8fafc",borderTop:`1px solid ${C.border}`}}>
                          <div style={{fontSize:13,fontWeight:700,color:C.text2,
                            marginBottom:12,display:"flex",
                            alignItems:"center",gap:8}}>
                            <TeamOutlined style={{color:C.blue}}/>
                            {displayEmps.length} employees in{" "}
                            <span style={{color:C.navy}}>{rec.cohortName}</span>
                            {rec.cohortDate&&
                              <span style={{color:C.text3,fontWeight:400}}>
                                · {dayjs(rec.cohortDate).format("DD MMM YYYY")}
                              </span>
                            }
                            {pendingEmps.length>0&&(
                              <span
                                onClick={e=>{e.stopPropagation();setShowPendingOnly(p=>!p);}}
                                style={{
                                  cursor:'pointer',fontWeight:600,fontSize:11,
                                  padding:'2px 10px',borderRadius:20,
                                  userSelect:'none',transition:'all .15s',
                                  color: showPendingOnly ? C.white : C.amber,
                                  background: showPendingOnly ? C.amber : C.lamber,
                                  border:`1px solid ${C.amber}`,
                                }}
                                title={showPendingOnly ? 'Click to show all employees' : 'Click to filter pending only'}
                              >
                                ⏳ {pendingEmps.length} pending{showPendingOnly?' · ✕ Clear':''}
                              </span>
                            )}
                            {showPendingOnly&&(
                              <span style={{fontSize:11,color:C.text3,fontWeight:400}}>
                                Showing pending only
                              </span>
                            )}
                          </div>
                          <Table
                            dataSource={displayEmps}
                            columns={empColumns}
                            rowKey="id" size="small"
                            scroll={{x:800}}
                            pagination={{
                              pageSize:10,
                              showTotal:t=>`${t} employees`,
                            }}
                            rowClassName={r=>r.isResigned
                              ?"row-resigned"
                              :r.calls?.some(c=>c.status==="No Response")
                                ?"row-at-risk":""}
                          />
                        </div>
                        );
                      },
                    }}
                    onRow={r=>({
                      onClick:()=>{
                        const key=`${r.cohortName}||${r.cohortDate}`;
                        if (expandedKey !== key) setShowPendingOnly(false);
                        setExpandedKey(prev=>prev===key?null:key);
                      },
                      style:{cursor:"pointer"},
                    })}
                  />
                </div>
          }

          {/* ══ ALL CLEAR BANNER ══════════════════════════════ */}
          {!loading && totals.pending===0 && totals.noResponse===0 && mainData.length>0 && (
            <div style={{
              background:"linear-gradient(135deg,#dcfce7 0%,#bbf7d0 100%)",
              border:`1.5px solid ${C.green}44`,borderRadius:14,
              padding:"18px 24px",marginTop:20,
              display:"flex",alignItems:"center",gap:16,
              boxShadow:"0 2px 12px rgba(22,163,74,0.12)",
            }}>
              <span style={{fontSize:36}}>🎉</span>
              <div>
                <div style={{fontWeight:800,fontSize:15,color:C.green}}>
                  All cohorts are on track!
                </div>
                <div style={{fontSize:12,color:"#166534",marginTop:3}}>
                  No pending calls or no-response employees across all {mainData.length} cohorts.
                  Outstanding engagement! 💪
                </div>
              </div>
            </div>
          )}

          {/* ══ DAY-10 Q&A SUMMARY (inline) ═══════════════════ */}
          {!loading && mainData.length>0 && (
            <div style={{marginTop:28}}>
              {/* Section divider */}
              <div style={{
                display:"flex",alignItems:"center",gap:14,marginBottom:20,
              }}>
                <div style={{flex:1,height:1,background:C.border}}/>
                <div style={{
                  display:"flex",alignItems:"center",gap:8,
                  padding:"6px 18px",borderRadius:20,
                  background:C.navy,color:"#fff",
                  fontSize:13,fontWeight:700,
                }}>
                  📋 Q&A Call Summary (Day-10 · Pratham / Day-30 · Campus)
                </div>
                <div style={{flex:1,height:1,background:C.border}}/>
              </div>
              <CohortDay10Summary/>
            </div>
          )}

        </>}

      </div>

      <style>{`
        .cohort-row-active > td { background:#eff6ff !important; }
        .row-resigned > td { background:#fef2f2 !important; opacity:.75; }
        .row-at-risk  > td { background:#fffbeb !important; }
        .ant-table-thead > tr > th {
          background:#f8fafc !important; color:#4a6580 !important;
          font-size:11px !important; font-weight:700 !important;
          text-transform:uppercase !important; letter-spacing:0.5px !important;
          border-bottom:1px solid #dce6f0 !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom:1px solid #dce6f080 !important;
          padding:10px 14px !important;
        }
        .ant-table-tbody > tr:hover > td { background:#f8faff !important; }
        .ant-card-head { border-bottom:none !important; }
        .ant-table-row-expand-icon-cell { display:none !important; }
      `}</style>
    </div>
  );
}